import { useCallback, useEffect, useRef, useState } from 'react';
import { Eye, Pencil, RotateCcw, Save, X } from 'lucide-react';
import { emptyCompanyProfile, type DocumentDefaults, type ReportTemplate, type TenantCompanyProfile } from '@forq/platform-data';
import type { OrderDocumentKind } from '@forq/orders-data';
import { buildOrderTemplateData, fillReportTemplate, orderDocumentLabels, previewSampleOrder, sampleTemplateMachine } from '@forq/orders/documents';

/**
 * Freier Beleg-Designer auf GrapesJS-Basis. Zwei Ebenen:
 *   - Grundbriefbogen: der geteilte Rahmen (Briefkopf, Anschrift/Meta, Fußzeile) mit dem Platzhalter
 *     {{beleg_inhalt}} an der Stelle, wo der belegtypische Inhalt sitzt. Gilt für ALLE Belegarten.
 *   - Je Belegart: nur der Inhalt (Anrede, Positionen, Summen …), der in den Slot des Grundbriefbogens
 *     kommt. Ohne Grundbriefbogen ist es die eigenständige Gesamtvorlage.
 * Platzhalter wie {{kunde.name}} werden beim Drucken gefüllt; die Positionstabelle wiederholt sich über
 * data-forq-repeat automatisch. Gespeichert wird html+css (Rendern) plus GrapesJS-Projekt (Wiederöffnen).
 * GrapesJS ist schwergewichtig und wird erst beim Öffnen dynamisch geladen.
 */

type DesignTarget = 'base' | OrderDocumentKind;
const designerKinds: readonly OrderDocumentKind[] = ['offer', 'confirmation', 'delivery-note', 'workshop-order'];

const positionsTable = `<table style="width:100%;border-collapse:collapse;font-size:11px;margin-top:3mm">
      <thead><tr>
        <th style="text-align:left;color:#0f6b5c;padding:6px 7px;border-bottom:1.5px solid #e30613">Pos.</th>
        <th style="text-align:left;color:#0f6b5c;padding:6px 7px;border-bottom:1.5px solid #e30613">Bezeichnung</th>
        <th style="text-align:right;color:#0f6b5c;padding:6px 7px;border-bottom:1.5px solid #e30613">Menge</th>
        <th style="text-align:right;color:#0f6b5c;padding:6px 7px;border-bottom:1.5px solid #e30613">Einzelpreis</th>
        <th style="text-align:right;color:#0f6b5c;padding:6px 7px;border-bottom:1.5px solid #e30613">Netto</th>
      </tr></thead>
      <tbody>
        <tr data-forq-repeat="positionen">
          <td style="padding:5px 7px;border-bottom:1px solid #e6e6e6">{{pos}}</td>
          <td style="padding:5px 7px;border-bottom:1px solid #e6e6e6">{{bezeichnung}}</td>
          <td style="padding:5px 7px;border-bottom:1px solid #e6e6e6;text-align:right">{{menge}} {{einheit}}</td>
          <td style="padding:5px 7px;border-bottom:1px solid #e6e6e6;text-align:right">{{einzelpreis}}</td>
          <td style="padding:5px 7px;border-bottom:1px solid #e6e6e6;text-align:right">{{netto}}</td>
        </tr>
      </tbody>
    </table>`;

const totalsTable = `<table style="margin:4mm 0 0 auto;font-size:11.5px"><tbody>
      <tr><td style="color:#444;padding-right:12px">Nettobetrag</td><td style="text-align:right;font-weight:700">{{summen.netto}}</td></tr>
      <tr><td style="color:#444;padding-right:12px">zzgl. USt</td><td style="text-align:right;font-weight:700">{{summen.ust}}</td></tr>
      <tr><td style="padding-right:12px;font-weight:800;border-top:1.5px solid #e30613">Gesamtbetrag</td><td style="text-align:right;font-weight:800;border-top:1.5px solid #e30613">{{summen.brutto}}</td></tr>
    </tbody></table>`;

/** Grundbriefbogen-Seed nach FTW-Vorlage, in drei beschriftete Zonen: Kopfzeile / Inhalt / Fußzeile.
 *  Die data-forq-zone-Marker labelt der Editor sichtbar (nur im Editor), der Renderer heftet die Fußzeile
 *  ans Blattende (siehe reportShell in @forq/orders/documents). */
function baseSeedHtml(): string {
  return `<div data-forq-page style="display:flex;flex-direction:column;min-height:262mm;flex:1">
  <div data-forq-zone="header">
    <div style="display:flex;justify-content:flex-end">
      <div style="text-align:right;line-height:1.4"><div style="font-size:20px;font-weight:800;color:#e30613">{{firma.name}}</div><div style="font-size:11px">{{firma.strasse}} · {{firma.plz}} {{firma.ort}}</div></div>
    </div>
    <hr style="border:0;border-top:3px solid #111;margin:6px 0 8mm">
    <div style="display:flex;justify-content:space-between;gap:12mm">
      <div style="font-size:12px;line-height:1.5">
        <div style="font-size:8px;color:#666;border-bottom:1px solid #ccc;padding-bottom:2px;margin-bottom:4px">{{firma.name}} · {{firma.strasse}} · {{firma.plz}} {{firma.ort}}</div>
        {{kunde.name}}<br>{{kunde.ansprechpartner}}<br>{{kunde.strasse}}<br>{{kunde.plz}} {{kunde.ort}}
      </div>
      <div style="min-width:72mm;border-left:3px solid #e30613;padding-left:10px">
        <div style="font-size:20px;font-weight:800">{{belegart}}</div>
        <table style="font-size:11px;margin-top:6px"><tbody>
          <tr><td style="color:#444;padding-right:14px">Nummer:</td><td style="font-weight:700">{{nummer}}</td></tr>
          <tr><td style="color:#444;padding-right:14px">Datum:</td><td style="font-weight:700">{{datum}}</td></tr>
          <tr><td style="color:#444;padding-right:14px">Kunden-Nr.:</td><td style="font-weight:700">{{kunde.nummer}}</td></tr>
        </tbody></table>
      </div>
    </div>
  </div>
  <div data-forq-zone="body" style="margin:8mm 0">{{beleg_inhalt}}</div>
  <div data-forq-zone="footer" style="margin-top:auto;border-top:1.5px solid #e30613;padding-top:5px;display:flex;gap:8mm;font-size:8px;color:#555;line-height:1.4">
    <div style="flex:1"><span style="color:#e30613;font-weight:700;display:block">{{firma.name}}</span>{{firma.strasse}}<br>{{firma.plz}} {{firma.ort}}</div>
    <div style="flex:1"><span style="color:#e30613;font-weight:700;display:block">Kontakt</span>Tel.: {{firma.telefon}}<br>{{firma.email}}<br>{{firma.web}}</div>
    <div style="flex:1"><span style="color:#e30613;font-weight:700;display:block">Bankverbindung</span>{{firma.bank}}<br>IBAN: {{firma.iban}}<br>BIC: {{firma.bic}}</div>
    <div style="flex:1"><span style="color:#e30613;font-weight:700;display:block">Unternehmen</span>Geschäftsführer: {{firma.geschaeftsfuehrer}}<br>{{firma.register}}<br>USt-IdNr.: {{firma.ustId}}</div>
  </div>
  </div>`;
}

/** Nur-Inhalt-Seed (kommt in den Slot des Grundbriefbogens): Anrede, Positionen, Summen, Eigentumsvorbehalt. */
function bodySeedHtml(): string {
  return `<p style="margin:0 0 4mm">Sehr geehrte Damen und Herren,</p>
  <p style="margin:0 0 3mm">nachfolgend bieten wir Ihnen die aufgeführten Positionen an.</p>
  ${positionsTable}
  ${totalsTable}
  <p style="font-size:10px;color:#444;margin-top:6mm">Die Ware bleibt bis zur vollständigen Bezahlung Eigentum der {{firma.name}}. Es gelten unsere AGB.</p>`;
}

/** Eigenständige Gesamtvorlage-Seed (ohne Grundbriefbogen): Kopf + Inhalt + Fußzeile in einem. */
function fullSeedHtml(): string {
  return `${baseSeedHtml().replace('{{beleg_inhalt}}', bodySeedHtml())}`;
}

function seedFor(target: DesignTarget, hasBase: boolean): string {
  if (target === 'base') return baseSeedHtml();
  return hasBase ? bodySeedHtml() : fullSeedHtml();
}

const machineTechTable = `<table style="border-collapse:collapse;font-size:11px">
  <tbody><tr data-forq-repeat="maschine.technik">
    <td style="padding:3px 12px 3px 0;color:#444">{{bezeichnung}}</td>
    <td style="padding:3px 0;font-weight:700">{{wert}}</td>
  </tr></tbody>
</table>`;

/**
 * Der Datenfeld-Explorer: die Belegdaten als Baum (Kategorie = Datengruppe, Eintrag = Feld), aus dem der
 * Gestalter Felder aufs Blatt zieht — das „Report-Builder-Gefühl". Kategorien erscheinen in dieser
 * Reihenfolge im Block-Manager. Reine Bausteine (Text/Bild …) stehen ganz unten.
 */
function forqBlocks(target: DesignTarget): Array<{ id: string; label: string; category: string; content: string }> {
  const field = (id: string, category: string, label: string, token: string) => ({ id, label, category, content: `<span>${token}</span>` });
  const blocks: Array<{ id: string; label: string; category: string; content: string }> = [
    // Beleg
    field('d-belegart', 'Beleg', 'Belegart', '{{belegart}}'),
    field('d-nummer', 'Beleg', 'Belegnummer', '{{nummer}}'),
    field('d-datum', 'Beleg', 'Datum', '{{datum}}'),
    field('d-vorgang', 'Beleg', 'Vorgangsnummer', '{{vorgangsnummer}}'),
    field('d-bearbeiter', 'Beleg', 'Bearbeiter', '{{bearbeiter}}'),
    // Kunde
    field('k-name', 'Kunde', 'Name', '{{kunde.name}}'),
    field('k-ansprech', 'Kunde', 'Ansprechpartner', '{{kunde.ansprechpartner}}'),
    field('k-adresse', 'Kunde', 'Anschrift (Block)', '{{kunde.name}}<br>{{kunde.ansprechpartner}}<br>{{kunde.strasse}}<br>{{kunde.plz}} {{kunde.ort}}'),
    field('k-nr', 'Kunde', 'Kunden-Nr.', '{{kunde.nummer}}'),
    // Firma
    field('fi-name', 'Firma', 'Name', '{{firma.name}}'),
    field('fi-adr', 'Firma', 'Anschrift', '{{firma.strasse}}, {{firma.plz}} {{firma.ort}}'),
    field('fi-tel', 'Firma', 'Telefon', '{{firma.telefon}}'),
    field('fi-mail', 'Firma', 'E-Mail', '{{firma.email}}'),
    field('fi-web', 'Firma', 'Web', '{{firma.web}}'),
    field('fi-bank', 'Firma', 'Bank / IBAN', '{{firma.bank}} · IBAN {{firma.iban}}'),
    field('fi-ustid', 'Firma', 'USt-IdNr.', '{{firma.ustId}}'),
    field('fi-gf', 'Firma', 'Geschäftsführer', '{{firma.geschaeftsfuehrer}}'),
    // Positionen
    { id: 'p-tabelle', label: 'Positionstabelle', category: 'Positionen', content: positionsTable },
    // Summen
    field('s-netto', 'Summen', 'Nettobetrag', '{{summen.netto}}'),
    field('s-ust', 'Summen', 'Umsatzsteuer', '{{summen.ust}}'),
    field('s-brutto', 'Summen', 'Gesamtbetrag', '{{summen.brutto}}'),
    { id: 's-tabelle', label: 'Summen (Block)', category: 'Summen', content: totalsTable },
    // Konditionen
    field('ko-zahlung', 'Konditionen', 'Zahlungsziel (Tage)', '{{konditionen.zahlungsziel}}'),
    field('ko-referenz', 'Konditionen', 'Ihre Referenz', '{{konditionen.referenz}}'),
    field('ko-liefer', 'Konditionen', 'Lieferbedingung', '{{konditionen.lieferbedingung}}'),
    field('ko-kost', 'Konditionen', 'Kostenstelle', '{{konditionen.kostenstelle}}'),
    // Maschine
    field('m-bez', 'Maschine', 'Bezeichnung', '{{maschine.bezeichnung}}'),
    field('m-nr', 'Maschine', 'Maschinennummer', '{{maschine.nummer}}'),
    field('m-hersteller', 'Maschine', 'Hersteller', '{{maschine.hersteller}}'),
    field('m-typ', 'Maschine', 'Typ', '{{maschine.typ}}'),
    field('m-seriennr', 'Maschine', 'Seriennummer', '{{maschine.seriennummer}}'),
    field('m-baujahr', 'Maschine', 'Baujahr', '{{maschine.baujahr}}'),
    { id: 'm-foto', label: 'Foto', category: 'Maschine', content: '<img src="{{maschine.foto}}" alt="Maschine" style="max-height:45mm;max-width:100%">' },
    { id: 'm-technik', label: 'Technische Daten (Tabelle)', category: 'Maschine', content: machineTechTable },
  ];
  if (target === 'base') {
    blocks.push({ id: 'z-slot', label: 'Beleg-Inhalt (Platzhalter)', category: 'Rahmen', content: '<div style="min-height:30mm">{{beleg_inhalt}}</div>' });
  }
  blocks.push(
    { id: 'ui-text', label: 'Text', category: 'Bausteine', content: '<div style="padding:4px">Text …</div>' },
    { id: 'ui-heading', label: 'Überschrift', category: 'Bausteine', content: '<h2 style="margin:4mm 0 2mm">Überschrift</h2>' },
    { id: 'ui-hr', label: 'Trennlinie', category: 'Bausteine', content: '<hr style="border:0;border-top:1px solid #333;margin:4mm 0">' },
    { id: 'ui-cols', label: 'Zwei Spalten', category: 'Bausteine', content: '<div style="display:flex;gap:10mm"><div style="flex:1">Links</div><div style="flex:1">Rechts</div></div>' },
    { id: 'ui-image', label: 'Bild', category: 'Bausteine', content: { type: 'image' } as unknown as string },
  );
  return blocks;
}

/** In das Editor-Canvas injiziert (NICHT exportiert): lässt das Zeichenbrett wie ein DIN-A4-Blatt aussehen
 *  und labelt die Zonen Kopfzeile/Inhalt/Fußzeile sichtbar. */
const canvasChromeCss = `
  html { background: #e9e9ec; }
  body { position: relative; width: 210mm; min-height: 297mm; margin: 12mm auto; padding: 16mm 16mm 14mm;
    background: #fff; box-shadow: 0 1px 12px rgba(0,0,0,0.25); box-sizing: border-box; display: flex; flex-direction: column; }
  /* Lineale mit cm-Teilung am oberen + linken Blattrand (nur Editor) */
  body::before { content: ""; position: absolute; top: 7mm; left: 16mm; right: 16mm; height: 3mm; pointer-events: none;
    background: repeating-linear-gradient(to right, #9aa0a6 0, #9aa0a6 0.25mm, transparent 0.25mm, transparent 10mm);
    border-bottom: 0.25mm solid #c8ccd1; }
  body::after { content: ""; position: absolute; top: 16mm; bottom: 14mm; left: 7mm; width: 3mm; pointer-events: none;
    background: repeating-linear-gradient(to bottom, #9aa0a6 0, #9aa0a6 0.25mm, transparent 0.25mm, transparent 10mm);
    border-right: 0.25mm solid #c8ccd1; }
  /* Bänder deutlich abgesetzt + farbig beschriftet */
  [data-forq-zone] { position: relative; outline: 1px dashed rgba(198,40,40,0.45); outline-offset: 3px; min-height: 10mm; padding-top: 4.5mm; }
  [data-forq-zone="header"] { background: rgba(30,90,170,0.045); }
  [data-forq-zone="footer"] { background: rgba(30,90,170,0.045); margin-top: auto; }
  [data-forq-zone="body"] { background: rgba(0,0,0,0.015); }
  [data-forq-zone]::before { position: absolute; top: 0; left: 0; font: 700 7.5px/1.3 "Segoe UI", Arial, sans-serif;
    letter-spacing: 0.1em; color: #fff; padding: 1.5px 6px; border-radius: 0 0 4px 0; pointer-events: none; }
  [data-forq-zone="header"]::before { content: "KOPFZEILE"; background: #1e5aaa; }
  [data-forq-zone="body"]::before { content: "INHALT"; background: #607d8b; }
  [data-forq-zone="footer"]::before { content: "FUSSZEILE"; background: #1e5aaa; }
`;

/** Chrome der gefüllten A4-Vorschau (Blatt + Fußzeile ans Blattende) — spiegelt die echte Ausgabe. */
const previewChromeCss = `
  * { box-sizing: border-box; } html, body { margin: 0; padding: 0; }
  body { background: #e9e9ec; padding: 10mm 0; font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; }
  .forq-sheet { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 14mm 16mm; background: #fff;
    box-shadow: 0 1px 10px rgba(0,0,0,0.22); display: flex; flex-direction: column; }
  [data-forq-zone="footer"] { margin-top: auto; }
`;

/** Der GrapesJS-Editor selbst (lädt die Bibliothek dynamisch beim Mounten). */
function DesignerCanvas({ target, template, hasBase, canEdit, profile, onSave, onRemove }: {
  readonly target: DesignTarget;
  readonly template: ReportTemplate | undefined;
  readonly hasBase: boolean;
  readonly canEdit: boolean;
  /** Das echte Firmenprofil des Mandanten — die Vorschau zeigt den eigenen Briefkopf. */
  readonly profile: TenantCompanyProfile;
  readonly onSave: (template: ReportTemplate) => void;
  readonly onRemove: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [savedAt, setSavedAt] = useState(false);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    let disposed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let editor: any = null;
    void (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const grapesjs = ((await import('grapesjs')) as any).default;
        await import('grapesjs/dist/css/grapes.min.css');
        if (disposed || !hostRef.current) return;
        editor = grapesjs.init({
          container: hostRef.current,
          height: '100%',
          fromElement: false,
          storageManager: false,
          blockManager: { blocks: forqBlocks(target) },
          styleManager: {
            sectors: [
              { name: 'Typografie', open: true, properties: ['font-family', 'font-size', 'font-weight', 'color', 'text-align', 'line-height'] },
              { name: 'Abstände & Größe', open: false, properties: ['width', 'height', 'padding', 'margin'] },
              { name: 'Hintergrund & Rahmen', open: false, properties: ['background-color', 'border', 'border-radius'] },
            ],
          },
        });
        editorRef.current = editor;
        if (template?.project) editor.loadProjectData(template.project);
        else editor.setComponents(seedFor(target, hasBase));
        // A4-Optik + Zonen-Labels ins Canvas (nur Editor-Ansicht, wird nicht exportiert) + Feld-Explorer offen.
        editor.onReady(() => {
          try {
            const canvasDoc = editor.Canvas.getDocument();
            if (canvasDoc && !canvasDoc.getElementById('forq-chrome')) {
              const styleEl = canvasDoc.createElement('style');
              styleEl.id = 'forq-chrome';
              styleEl.textContent = canvasChromeCss;
              canvasDoc.head.appendChild(styleEl);
            }
            editor.runCommand('open-blocks'); // Datenfeld-Explorer gleich sichtbar
          } catch { /* Canvas noch nicht bereit -- unkritisch */ }
        });
        setStatus('ready');
      } catch {
        if (!disposed) setStatus('error');
      }
    })();
    return () => { disposed = true; if (editor) editor.destroy(); editorRef.current = null; };
  }, [target, template, hasBase]);

  const save = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    onSave({ html: editor.getHtml(), css: editor.getCss(), project: editor.getProjectData() });
    setSavedAt(true);
    window.setTimeout(() => setSavedAt(false), 1500);
  }, [onSave]);

  // Aus dem aktuellen Editor-Zustand ein gefülltes A4-Blatt mit Beispieldaten bauen (WYSIWYG-Vorschau).
  const buildPreviewHtml = useCallback((): string => {
    const editor = editorRef.current;
    if (!editor) return '';
    // Beim Grundbriefbogen den eingebauten Beispiel-Inhalt in den Slot setzen, damit man das Ganze sieht.
    const bodyHtml = target === 'base'
      ? editor.getHtml().split('{{beleg_inhalt}}').join(bodySeedHtml())
      : editor.getHtml();
    const data = buildOrderTemplateData({
      kind: target === 'base' ? 'offer' : target, documentNumber: 'AN-2026-00042',
      order: previewSampleOrder, company: previewCompany(profile), issuedIso: '2026-08-01', machine: sampleTemplateMachine,
    });
    const filled = fillReportTemplate(bodyHtml, data);
    return `<!doctype html><html lang="de"><head><meta charset="utf-8"><style>${previewChromeCss}${editor.getCss()}</style></head><body><div class="forq-sheet">${filled}</div></body></html>`;
  }, [target, profile]);

  const togglePreview = useCallback(() => {
    setMode((current) => {
      if (current === 'edit') { setPreviewHtml(buildPreviewHtml()); return 'preview'; }
      return 'edit';
    });
  }, [buildPreviewHtml]);

  const hint = target === 'base'
    ? 'Grundbriefbogen: Briefkopf, Anschrift, Fußzeile – gilt für alle Belege. Der Baustein „Beleg-Inhalt" markiert, wo der belegtypische Teil erscheint.'
    : hasBase
      ? 'Nur der Inhalt dieser Belegart – Briefkopf und Fußzeile kommen vom Grundbriefbogen.'
      : 'Gesamtvorlage dieser Belegart (kein Grundbriefbogen gesetzt).';

  return (
    <div className="admin-report">
      <div className="admin-report__bar">
        <span className="admin-report__hint">{mode === 'preview' ? 'Beispiel-Vorschau mit Musterdaten – so sieht der fertige Beleg aus.' : hint} Platzhalter wie <code>{'{{kunde.name}}'}</code>; die Positionstabelle wiederholt sich automatisch.</span>
        <div className="admin-report__actions">
          <button type="button" className={`admin-report__toggle${mode === 'preview' ? ' is-active' : ''}`} disabled={status !== 'ready'} onClick={togglePreview}>
            {mode === 'edit' ? <><Eye size={13} /> Vorschau</> : <><Pencil size={13} /> Bearbeiten</>}
          </button>
          {template && <button type="button" className="admin-company__ghost" disabled={!canEdit} onClick={onRemove}><RotateCcw size={13} /> {target === 'base' ? 'Grundbriefbogen entfernen' : 'Vorlage entfernen'}</button>}
          <button type="button" className="admin-report__save" disabled={!canEdit || status !== 'ready'} onClick={save}>
            <Save size={13} /> {savedAt ? 'Gespeichert' : 'Speichern'}
          </button>
        </div>
      </div>
      {status === 'error' && <p className="admin-company__note">Der Designer konnte nicht geladen werden.</p>}
      <div className="admin-report__canvas">
        <div ref={hostRef} className="admin-report__host" style={{ display: mode === 'edit' ? undefined : 'none' }} />
        {mode === 'preview' && <iframe className="admin-report__preview" title="Beispiel-Vorschau" srcDoc={previewHtml} />}
      </div>
    </div>
  );
}

/**
 * Platzhalter für Felder, die der Mandant noch nicht ausgefüllt hat.
 *
 * Hier stand bis 2026-09-06 die echte Firmierung von Fördertechnik Werl —
 * inklusive Handelsregisternummer, USt-IdNr. `DE348642229`, Name des
 * Geschäftsführers und echter IBAN. Das lag im Bündel, das JEDER Mandant lädt,
 * und wäre jedem Fremdkunden angezeigt worden, der den Vorlagen-Editor öffnet.
 *
 * Die Werte sind bewusst erkennbar erfunden: eine Vorschau soll zeigen, wie ein
 * Beleg aussieht, nicht so tun, als sei sie ein echter Beleg. Die IBAN ist eine
 * gültige Prüfziffer über einer Nullbank, damit sie nirgends hinführt.
 */
const previewPlaceholderCompany = {
  identity: {
    name: 'Musterfirma GmbH', legalForm: 'GmbH',
    address: { street: 'Musterweg 1', postalCode: '12345', city: 'Musterstadt', countryCode: 'DE' },
    register: 'AG Musterstadt · HRB 00000', registerCourt: 'AG Musterstadt',
    managingDirectors: 'Vorname Nachname', vatId: 'DE000000000', taxNumber: '',
  },
  contact: {
    phone: '+49 000 0000000', fax: '', email: 'info@musterfirma.example',
    website: 'www.musterfirma.example', contactName: '', contactPhone: '', contactEmail: '',
  },
  bank: { bankName: 'Musterbank', iban: 'DE02 1203 0000 0000 2020 51', bic: 'BYLADEM1001' },
  letterhead: emptyCompanyProfile.letterhead,
  documentDefaults: emptyCompanyProfile.documentDefaults,
  datev: emptyCompanyProfile.datev,
  dunning: emptyCompanyProfile.dunning,
} satisfies TenantCompanyProfile;

/**
 * Die Vorschau zeigt den EIGENEN Briefkopf, nicht den einer fremden Firma.
 *
 * Leere Felder werden aus dem Platzhalter aufgefüllt, damit der Bogen auch bei
 * einer frisch eingerichteten Installation vollständig aussieht. Was der Kunde
 * gepflegt hat, gewinnt immer.
 */
function previewCompany(profile: TenantCompanyProfile): TenantCompanyProfile {
  /** Eigenes gewinnt; nur Leeres wird aufgefüllt. */
  const oder = (eigen: string, ersatz: string): string => (eigen.trim() === '' ? ersatz : eigen);

  const p = previewPlaceholderCompany;
  return {
    ...profile,
    identity: {
      ...profile.identity,
      name: oder(profile.identity.name, p.identity.name),
      legalForm: oder(profile.identity.legalForm, p.identity.legalForm),
      register: oder(profile.identity.register, p.identity.register),
      registerCourt: oder(profile.identity.registerCourt, p.identity.registerCourt),
      managingDirectors: oder(profile.identity.managingDirectors, p.identity.managingDirectors),
      vatId: oder(profile.identity.vatId, p.identity.vatId),
      address: {
        ...profile.identity.address,
        street: oder(profile.identity.address.street, p.identity.address.street),
        postalCode: oder(profile.identity.address.postalCode, p.identity.address.postalCode),
        city: oder(profile.identity.address.city, p.identity.address.city),
        countryCode: oder(profile.identity.address.countryCode, p.identity.address.countryCode),
      },
    },
    contact: {
      ...profile.contact,
      phone: oder(profile.contact.phone, p.contact.phone),
      email: oder(profile.contact.email, p.contact.email),
      website: oder(profile.contact.website, p.contact.website),
    },
    bank: {
      ...profile.bank,
      bankName: oder(profile.bank.bankName, p.bank.bankName),
      iban: oder(profile.bank.iban, p.bank.iban),
      bic: oder(profile.bank.bic, p.bank.bic),
    },
  };
}

/** Auswahl Grundbriefbogen / Belegart + Öffnen-Schalter; GrapesJS wird erst beim Öffnen geladen. */
export function ReportDesignerSection({ profile, canEdit, onPatch }: {
  readonly profile: TenantCompanyProfile;
  readonly canEdit: boolean;
  readonly onPatch: (patch: Partial<DocumentDefaults>) => void;
}) {
  const [target, setTarget] = useState<DesignTarget>('base');
  const [open, setOpen] = useState(false);
  const templates = profile.documentDefaults.reportTemplates;
  const base = profile.documentDefaults.reportBase;
  const hasBase = !!(base && base.html.trim());
  const current = target === 'base' ? base : templates[target];

  const save = useCallback((next: ReportTemplate) => {
    if (target === 'base') onPatch({ reportBase: next });
    else onPatch({ reportTemplates: { ...templates, [target]: next } });
  }, [target, templates, onPatch]);
  const remove = useCallback(() => {
    if (target === 'base') onPatch({ reportBase: undefined });
    else { const rest = { ...templates }; delete rest[target]; onPatch({ reportTemplates: rest }); }
    setOpen(false);
  }, [target, templates, onPatch]);

  const selectTarget = (next: DesignTarget) => { setTarget(next); };

  const tabs = (
    <div className="admin-company__preview-tabs">
      <button type="button" className={target === 'base' ? 'is-active' : ''} onClick={() => selectTarget('base')}>
        Grundbriefbogen{hasBase ? ' •' : ''}
      </button>
      <span className="admin-company__preview-sep" />
      {designerKinds.map((designerKind) => (
        <button key={designerKind} type="button" className={target === designerKind ? 'is-active' : ''} onClick={() => selectTarget(designerKind)}>
          {orderDocumentLabels[designerKind]}{templates[designerKind] ? ' •' : ''}
        </button>
      ))}
    </div>
  );

  return (
    <div className="admin-company__layout is-wide">
      <div className="admin-company__preview-head">
        <span>Report-Designer (frei gestalten)</span>
        {tabs}
      </div>
      <p className="admin-company__note">
        {target === 'base'
          ? <>Der <strong>Grundbriefbogen</strong> (Briefkopf + Fußzeile) gilt für alle Belege – eine Änderung schlägt überall durch. Setze mit dem Baustein „Beleg-Inhalt", wo der belegtypische Teil erscheint.</>
          : <>Inhalt für <strong>{orderDocumentLabels[target]}</strong>. {hasBase ? 'Briefkopf/Fußzeile kommen vom Grundbriefbogen.' : 'Ohne Grundbriefbogen ist dies die Gesamtvorlage.'} Ein <strong>•</strong> markiert, was schon gestaltet ist.</>}
      </p>
      <button type="button" className="admin-report__open" disabled={!canEdit} onClick={() => setOpen(true)}>
        <X size={14} style={{ transform: 'rotate(45deg)' }} /> {current ? 'Bearbeiten' : (target === 'base' ? 'Grundbriefbogen erstellen' : 'Designer öffnen')} – im Vollbild
      </button>
      {open && (
        <div className="admin-report-modal" role="dialog" aria-modal="true" aria-label="Report-Designer">
          <div className="admin-report-modal__head">
            <span className="admin-report-modal__title">Report-Designer · {target === 'base' ? 'Grundbriefbogen' : orderDocumentLabels[target]}</span>
            {tabs}
            <button type="button" className="admin-report-modal__close" onClick={() => setOpen(false)}>Schließen <X size={15} /></button>
          </div>
          <div className="admin-report-modal__body">
            <DesignerCanvas target={target} template={current} hasBase={hasBase} canEdit={canEdit} profile={profile} onSave={save} onRemove={remove} />
          </div>
        </div>
      )}
    </div>
  );
}
